import React from 'react' // eslint-disable-line no-unused-vars
import PaginatedDocumentsTable from '../tables/PaginatedDocumentsTable'
import { connect } from 'react-redux'
import { NO_NAME_DISPLAYED, DOWNLOAD_BUTTON } from '../../constants'
import StaticConfig from '../StaticConfig'
import { ABBVIE_EMPLOYEE, REF_DATA, USER_SCOPE } from '../../constants'
import { addNotification as notify } from 'reapop'
import { triggerSSO } from '../InitiateSSO'
import { api } from '../index.api'
import { auditSearch } from '../Audit'
import { CircularProgress } from '@material-ui/core'
import { v4 as uuidv4 } from 'uuid'
import { getApplicationRegex } from '../plugins/utils'

class ProductDocumentSearch extends React.Component {
    constructor(props) {
        super(props)

        // Ref used to focus on search input after reset button is clicked
        this.searchInputRef = React.createRef();

        this.state = {
            searchText: '',
            searchPerformed: false,
            searching: false,
            searchResults: [],
            searchId: uuidv4(),
            searchAuditId: null
        }

        // Keys that identify data elements to include in the table columns,
        // with associated formatting
        this.columnKeys = [
            {
                key: 'name__v',
                style: { fontWeight: 'bold' },
                width: '60%'
            },
            {
                key: 'gmi_approval_date__c',
                style: { whiteSpace: 'nowrap' },
                width: '15%'
            },
            {
                key: 'language__v',
                isArray: true,
                width: '10%'
            },
            {
                key: DOWNLOAD_BUTTON,
                style: { textAlign: 'right' },
                width: '15%'
            }
        ]
    }

    handleSearchChange = (e) => {
        const inputValue = e.target.value.toLowerCase();
        const ref_Data = this.props.referenceData.referenceData;
        const regex = getApplicationRegex(ref_Data);
        if (regex.Emoji_Regex !== null && regex.Emoji_Regex.test(inputValue)) {
        // Emoji detected, prevent it from being added
        this.showNotification(
          "Error!",
          "Special characters are not allowed",
          "error"
        );
        return;
      } else if (regex.Script_Regex !== null && regex.Script_Regex.test(inputValue)) {
        // HTML Tags and Script detected, prevent it from being added
        this.showNotification(
          "Error!",
          "Submission fails. Input contains prohibited words. Please remove them and try again.",
          "error"
        );
        return;
      } else if (regex.Script_Regex === null || regex.Emoji_Regex === null) {
        this.showNotification(
          "Error!",
          "Validation configuration is missing. Please contact support.",
          "error"
        );
        return;
      }
    this.setState({ searchText: e.target.value.replace(/\*/g, '') })}

    handleSearchReset = () => this.setState({
        searchText: '',
    }, () => this.searchInputRef.current.focus())

    handleSearchSubmit = (e) => {
        e.preventDefault()
        if (!this.state.searchText) {
            return
        }

        // Country check in case AbbVie employees are ever allowed to access Patient Portal
        const country = StaticConfig.Context.UserType === ABBVIE_EMPLOYEE
            ? StaticConfig.Context.ssoCountry.name
            : StaticConfig.Context.Country.name

        // Format user input words as an array so that search API applies "OR" criteria instead of "AND"
        const keywords = this.state.searchText
            .trim().toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ').split(' ')
        const requestParams = {
            product: this.props.product.veevaProductCode,
            searchUserType: StaticConfig.Context.UserType,
            keywords,
            country,
            type: null // No specific document type filtering for product document search
        }

        this.setState({ searching: true })
        api.searchPatientDocuments(
            requestParams,
            response => this.searchSuccessHandler(requestParams, response),
            this.searchFailureHandler
        )
    }

    searchSuccessHandler = (requestParams, response) => {
        if (response.status === 200) {
            response.json().then(searchResults => {
                this.setState({
                    searching: false,
                    searchPerformed: true,
                    searchResults,
                    searchId: uuidv4()
                })
                this.auditSearchEvent(requestParams, searchResults)
            })
        } else if (response.status === 401) {
            // In case AbbVie employees are ever allowed to access Patient Portal
            this.showNotification(
                'Error',
                // TODO: translate this for Patient and HCP portals
                'You are not authorized to perform this action. Please log in again.',
                'error'
            )
            setTimeout(() => triggerSSO(this.props.pageContent.content.lang), 3000)
        } else {
            this.showNotification(
                'Error',
                // TODO: translate this for Patient and HCP portals
                'Error occurred while submitting query',
                'error'
            )
            this.setState({ searching: false })
        }
    };

    searchFailureHandler = error => {
        this.setState({ searching: false })
        // TODO: translate this for Patient and HCP portals
        this.showNotification('Error', 'Error occurred while submitting query', 'error')
    };

    auditSearchEvent = (requestParams, result) => {
        const productDetails = this.findProductDetails();
        const searchCriteria = {
            productName: productDetails.name,
            productCode: productDetails.productCode,
            category: '',
            country: requestParams.country,
            scope: USER_SCOPE[requestParams.searchUserType],
            keyword: requestParams.keywords.join(', ')
        }

        auditSearch(searchCriteria, result, this.setSearchAuditId)
    };

    setSearchAuditId = searchAuditId => {
        this.setState({ searchAuditId })
    };

    findProductDetails = () => {
        if (this.props.referenceData) {
            const selectedProduct = this.props.referenceData.referenceData.ProductMaster
                .find(p => p.id === this.props.product.productMasterId)
            if (selectedProduct) {
                return selectedProduct
            }
        }
        return null
    };

    showNotification = (title, message, status) => {
        this.props.notify({
            title,
            message,
            status,
            position: 'tr',
            dismissible: true,
            dismissAfter: 3000
        })
    }

    render() {
        const { pageContent, product, showMobileVersion } = this.props
        const languageContent = pageContent.content.module.products.details
        const searchContent = pageContent.content.module.searchDocument

        const columnNames = [
            searchContent.documentTitle,
            searchContent.effectiveDate,
            searchContent.language,
            NO_NAME_DISPLAYED
        ]

        return (
            <React.Fragment>
                <h3 className='section-header' >
                    {`${languageContent.productBeforeText} ${product.brandName} ${languageContent.productAfterText}`}
                </h3>
                <form onSubmit={this.handleSearchSubmit} className='mb-3'>
                    <div className='search-container'>
                        <div className='search-input-container'>
                            <div className='input-group search-group'>
                                <input
                                    type='search'
                                    autoComplete='on'
                                    className='search-bar form-control'
                                    id='productDocSearchBar'
                                    name='productDocSearchBar'
                                    maxLength='150'
                                    value={this.state.searchText}
                                    placeholder={languageContent.enterKeywords}
                                    aria-label={languageContent.enterKeywords}
                                    onChange={this.handleSearchChange}
                                    ref={this.searchInputRef}
                                    disabled={this.state.searching}
                                />
                                {
                                    this.state.searchText &&
                                    <button
                                        id='reset'
                                        className='reset-button'
                                        type='button'
                                        aria-label='Clear Value'
                                        onClick={this.handleSearchReset}
                                        disabled={this.state.searching}
                                    >
                                        <i className="fa fa-times" />
                                    </button>
                                }
                            </div>
                        </div>
                        <div className='btn-search-product'>
                            <button
                                type='submit'
                                id='submit'
                                aria-label='Search'
                                className='btn btn-primary btn-rounded btn-lg-wide search-btn'
                                disabled={this.state.searching || !this.state.searchText}
                            >
                                {
                                    this.state.searching
                                        ? <div className='loading-text-centered'>
                                            <span>{searchContent.search}</span>
                                            <span className='loading-progress-centered'><CircularProgress size={24} /></span>
                                        </div>
                                        : <React.Fragment>{searchContent.search}</React.Fragment>
                                }
                            </button>
                        </div>
                    </div>
                </form>
                {
                    (this.state.searchResults.length === 0 && this.state.searchPerformed && !this.state.searching) &&
                    <div className='search-error-container'>
                        <div className='warning-message'>
                            <i className='fa fa-times-circle' />{languageContent.noDocumentsFound}
                        </div>
                    </div>
                }
                {
                    this.state.searchResults.length > 0 &&
                    <div className='mobile-background'>
                        <div className='mobile-padding'>
                            <PaginatedDocumentsTable
                                // Key forces pagination component to reset state when search result changes
                                key={this.state.searchId}
                                id='search-documentation'
                                results={this.state.searchResults}
                                searchedProductCode={product.veevaProductCode}
                                columnNames={columnNames}
                                columnKeys={this.columnKeys}
                                searchAuditId={this.state.searchAuditId}
                                showMobileVersion={showMobileVersion}
                            />
                            <p>
                                <strong>{searchContent.piSearchResultBeforeNote}</strong> {searchContent.piSearchResultNote}
                            </p>
                        </div>
                    </div>
                }
            </React.Fragment>
        )
    }
}

const mapStateToProps = state => ({
    pageContent: state.pageContent,
    referenceData: state.referenceData.find(category => category.type === REF_DATA),
})

export default connect(mapStateToProps, { notify })(ProductDocumentSearch)



// WEBPACK FOOTER //
// ./src/mi-app/products/ProductDocumentSearch.js